/**
 * Vanilla JS star rating component.
 * Discovers elements with [data-rating] and renders star icons.
 *
 * Readonly mode (default):
 *   <div data-rating="3.5" data-rating-max="5"></div>
 *   Renders filled/half/empty stars.
 *
 * Interactive mode:
 *   <div data-rating="0" data-rating-interactive data-rating-name="review_rating"></div>
 *   Renders clickable stars with a hidden input for form submission.
 *
 * Attributes:
 *   data-rating             - current rating value (number)
 *   data-rating-max         - max stars (default 5)
 *   data-rating-interactive - presence enables click-to-rate
 *   data-rating-name        - name attribute for the hidden input
 *   data-rating-color       - star fill color (default "#cc9e67")
 *   data-rating-size        - star size in px (default 20)
 */

function createStarSVG(filled, half, color, size) {
    if (filled) {
        return `<svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="${color}" xmlns="http://www.w3.org/2000/svg" style="display:inline-block;vertical-align:middle;"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>`;
    }
    if (half) {
        return `<svg width="${size}" height="${size}" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" style="display:inline-block;vertical-align:middle;">
            <defs><clipPath id="half-clip"><rect x="0" y="0" width="12" height="24"/></clipPath></defs>
            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" fill="#d4d4d4"/>
            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" fill="${color}" clip-path="url(#half-clip)"/>
        </svg>`;
    }
    return `<svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="#d4d4d4" xmlns="http://www.w3.org/2000/svg" style="display:inline-block;vertical-align:middle;"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>`;
}

function renderStars(el) {
    const rating = parseFloat(el.dataset.rating) || 0;
    const max = parseInt(el.dataset.ratingMax, 10) || 5;
    const color = el.dataset.ratingColor || '#cc9e67';
    const size = parseInt(el.dataset.ratingSize, 10) || 20;
    const interactive = el.hasAttribute('data-rating-interactive');
    const inputName = el.dataset.ratingName || 'rating';

    el.innerHTML = '';
    el.style.display = 'inline-flex';
    el.style.gap = '2px';
    el.style.alignItems = 'center';

    if (interactive) {
        // Create hidden input
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = inputName;
        input.value = rating;
        el.appendChild(input);
    }

    for (let i = 1; i <= max; i++) {
        const starWrapper = document.createElement('span');
        starWrapper.style.cursor = interactive ? 'pointer' : 'default';

        let filled = false;
        let half = false;

        if (i <= Math.floor(rating)) {
            filled = true;
        } else if (i === Math.ceil(rating) && rating % 1 >= 0.25 && rating % 1 <= 0.75) {
            half = true;
        } else if (i === Math.ceil(rating) && rating % 1 > 0.75) {
            filled = true;
        }

        starWrapper.innerHTML = createStarSVG(filled, half, color, size);

        if (interactive) {
            starWrapper.addEventListener('click', () => {
                el.dataset.rating = i;
                const hiddenInput = el.querySelector('input[type="hidden"]');
                if (hiddenInput) hiddenInput.value = i;
                renderStars(el);
            });

            starWrapper.addEventListener('mouseenter', () => {
                const stars = el.querySelectorAll('span');
                stars.forEach((s, idx) => {
                    const starIdx = idx; // 0-based
                    if (starIdx < i) {
                        s.innerHTML = createStarSVG(true, false, color, size);
                    } else {
                        s.innerHTML = createStarSVG(false, false, color, size);
                    }
                });
            });

            el.addEventListener('mouseleave', () => {
                renderStars(el);
            });
        }

        el.appendChild(starWrapper);
    }
}

function initAll(root) {
    const elements = (root || document).querySelectorAll('[data-rating]');
    elements.forEach((el) => renderStars(el));
}

// Auto-init on DOMContentLoaded
document.addEventListener('DOMContentLoaded', () => initAll());

// Re-init after HTMX swaps
document.body.addEventListener('htmx:afterSettle', (e) => {
    initAll(e.detail.target);
});

// Export for manual use
window.initStarRatings = initAll;
